import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { createAxiosInstance } from "../axiosSettings";
import { logout } from "./authSlice";

const initialState = {
  isLoading: false,
  permissions : [],
  roles : [],
  subAdmins : [],
  permission : null,
  rolePermission : null,
  userData : null,
  error : null
};


export const PERMISSION_URL = "admin/role/permissions";
export const ROLE_URL = "admin/role";

export const getPermissions = createAsyncThunk(
    PERMISSION_URL + "/get",
    async (formData, { rejectWithValue }) => {
      const axiosInstance = await createAxiosInstance();
      try {
        const response = await axiosInstance.get(PERMISSION_URL);
        if (response.data) {
          return response.data;
        } else {
          return rejectWithValue(response.data.message || "Error");
        }
      } catch (error) {
        return rejectWithValue(
          error.response?.data?.message || "Error"
        );
      }
    }
);

export const getRoles = createAsyncThunk(
  ROLE_URL + "/get",
  async (formData, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      const response = await axiosInstance.get(ROLE_URL);
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

export const getRolePermissionById = createAsyncThunk(
  ROLE_URL + "/get-role-permission",
  async (id, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      const response = await axiosInstance.get(ROLE_URL + "/permission/" + id);
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

export const createRoleWithPermissions = createAsyncThunk(
  PERMISSION_URL + "/create",
  async (formData, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      const response = await axiosInstance.post(PERMISSION_URL, formData);
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

export const updateRoleWithPermissions = createAsyncThunk(
  ROLE_URL + "/updates",
  async (formData, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      const id = formData?.id;
      const response = await axiosInstance.post(ROLE_URL + "/permission/" + id, formData);
      if (response.data) {
        return response.data?.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

export const createUserAndAssignRole = createAsyncThunk(
  ROLE_URL + "/create-user",
  async (formData, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      const response = await axiosInstance.post(`${ROLE_URL}/create-user`, formData);
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

export const getSubAdmins = createAsyncThunk(
  ROLE_URL + "/get-sub-admins",
  async (formData, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      const response = await axiosInstance.get(`${ROLE_URL}/get-sub-admins`, {
        params: formData
      });
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

export const deleteRole = createAsyncThunk(
  ROLE_URL + '/delete-role',
  async (id, { rejectWithValue }) => {
    const axiosInstance = await createAxiosInstance();
    try {
      // const id = formData.get('id')
      const response = await axiosInstance.delete(ROLE_URL + '/delete-role/' + id);
      if (response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.data.message || "Error");
      }
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

const roleSlice = createSlice({
    name : "role",
    initialState,
    extraReducers : (builder) => {
        builder
            .addCase(getPermissions.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.permissions = action.payload;
            })
            .addCase(getPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(createRoleWithPermissions.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(createRoleWithPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.permission = action.payload;
            })
            .addCase(createRoleWithPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(getRoles.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(getRoles.fulfilled, (state, action) => {
                state.isLoading = false;
                state.roles = action.payload;
            })
            .addCase(getRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(getRolePermissionById.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(getRolePermissionById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.rolePermission = action.payload;
            })
            .addCase(getRolePermissionById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(updateRoleWithPermissions.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(updateRoleWithPermissions.fulfilled, (state, action) => {
                state.isLoading = false;
                state.rolePermission = action.payload;
            })
            .addCase(updateRoleWithPermissions.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(createUserAndAssignRole.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(createUserAndAssignRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.userData = action.payload;
            })
            .addCase(createUserAndAssignRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(getSubAdmins.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(getSubAdmins.fulfilled, (state, action) => {
                state.isLoading = false;
                state.subAdmins = action.payload;
            })
            .addCase(getSubAdmins.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(deleteRole.pending, (state) => {
              state.isLoading = true;
              state.error = null;
            })
            .addCase(deleteRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.permission = action.payload;
            })
            .addCase(deleteRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(logout, () => initialState)
    }
})

export const hasPermission = (user, permission) => {
  let permissions = []
  user?.role?.permissions.forEach((data) => {
    permissions.push(data?.name)
  })
  return user?.role_type == "admin" ? true : permissions?.includes(permission);
};

export default roleSlice.reducer